import { IPatientListDto, ISurveyDto, SurveyType, TableColumnsEnum } from '../../services/api/api-client';
import { ColumnDef } from '@tanstack/react-table';
import {
  archiveColumn,
  exportColumn,
  useCategoryColumn,
  useFlexiblePlaceholderColumn,
  useIndicatorColumns,
  useLastNoteColumn,
  useRecentChangesColumn,
  useStageColumn,
  useSurveyColumns,
  useUniqueIdColumn,
} from './columns/columns.constants';
import { conditionalConcat } from '../../helpers/arrayHelpers';
import { expandRowButtonColumn } from 'uikit/table/columns/ExpandRowButtonColumn';
import { useMemo } from 'react';
import { useStudy } from 'src/helpers/hooks/useStudy';

export type PatientTableColumnsOptions = {
  onClick?: (survey: SurveyType, patient: string, headerTitle: string, answId?: number | null) => void;
  tableColumns: TableColumnsEnum;
  surveys: ISurveyDto[];
};

export const usePatientTableColumns = (options: PatientTableColumnsOptions): ColumnDef<IPatientListDto>[] => {
  let nextIsColored = false;
  const study = useStudy();
  const getIsColored = () => {
    const res = nextIsColored;
    nextIsColored = !nextIsColored;
    return res;
  };

  const columnOptions = {
    getIsColored,
    tableColumns: options.tableColumns,
  };

  const uniqueIdColumn = useUniqueIdColumn(columnOptions);
  const indicatorColumns = useIndicatorColumns(columnOptions);
  const surveyColumns = useSurveyColumns({ ...columnOptions, onClick: options.onClick, surveys: options.surveys });
  const categoryColumn = useCategoryColumn(columnOptions);
  const stageColumn = useStageColumn(columnOptions);
  const lastNoteColumn = useLastNoteColumn(columnOptions);
  const recentChangesColumn = useRecentChangesColumn(columnOptions);
  const flexiblePlaceholderColumn = useFlexiblePlaceholderColumn(columnOptions);

  return useMemo(() => {
    return conditionalConcat(
      [uniqueIdColumn],
      indicatorColumns,
      surveyColumns,
      categoryColumn && [categoryColumn],
      stageColumn && [stageColumn],
      lastNoteColumn && [lastNoteColumn],
      recentChangesColumn && [recentChangesColumn],
      flexiblePlaceholderColumn && [flexiblePlaceholderColumn],
      study?.hasEcrf && [archiveColumn()],
      [exportColumn()],
      [expandRowButtonColumn({})],
    );
  }, [
    categoryColumn,
    flexiblePlaceholderColumn,
    indicatorColumns,
    lastNoteColumn,
    recentChangesColumn,
    stageColumn,
    study?.hasEcrf,
    surveyColumns,
    uniqueIdColumn,
  ]);
};
